import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {ArticleDetailService_GetMinimalProductBulkLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.grpc.lazy';
import {GetMinimalProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useParams} from '@aboutyou/router';
import {useBasketToken, useBasketTokenAwaitedAnySession} from '@aboutyou/user';
import {useEffect} from 'react';
import {useActivatedCouponState} from '../../../../features/Coupons/useActivatedCouponState';
import {useCouponPromotionIdFromQueryParams} from '../../../../features/Coupons/useCouponPromotionIdFromQueryParams';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {
  SelectedSize,
  SetSelectedSize,
} from '../../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useProtoProductImageType} from '../../../../shared-deprecated/useProductImageType';
import {useSelectedTargetGroup} from '../../../../shared-deprecated/useSelectedTargetGroup';
import {getCurrentSelectedSizeFromSizesSection} from '../../utils/getCurrentSelectedSizeFromSizesSection';
import {useGetBadgesFromBulkRequest} from '../../utils/useGetBadgesFromBulkRequest';
import {useKeepPreviousData} from '../../utils/useKeepPreviousData';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const useGetMinimalProductBulkSection = <
  Section extends keyof GetMinimalProductBulkResponse,
>(
  section: Section,
  productId: number,
  disabled?: boolean,
) => {
  const {token: basketToken} = useBasketToken();
  const {session, refetchesDisabled} = useBasketTokenAwaitedAnySession();
  const keepPreviousData = useKeepPreviousData({
    productId,
    session,
  });
  const {targetGroup} = useSelectedTargetGroup();

  const {couponPromotionId: activatedCouponPromotionId} =
    useActivatedCouponState(productId);
  const couponPromotionIdFromQueryParams =
    useCouponPromotionIdFromQueryParams();
  const {promotionId: couponPromotionIdFromPathParams} = useParams();

  /**
   * When de-selecting a coupon, we set the activatedCouponPromotionId to null explicitly,
   * so that we can distinguish between two states:
   * 1. Coupon is not selected  initially (activatedCouponPromotionId is undefined)
   * 2. Coupon is de-selected manually (activatedCouponPromotionId is null)
   *
   * If the activatedCouponPromotionId is undefined, we can use the coupon from url path params or query params.
   * If the activatedCouponPromotionId is null, that means the user has de-selected the coupon -> in this case we should not pass selectedCouponPromotionId at all,
   * so that the backend will return the product without any coupon applied.
   */
  const selectedCouponPromotionId =
    activatedCouponPromotionId === null
      ? undefined
      : couponPromotionIdFromQueryParams ||
        activatedCouponPromotionId ||
        couponPromotionIdFromPathParams;

  return useGrpcRequest(
    ArticleDetailService_GetMinimalProductBulkLazy,
    {
      productId,
      session,
      targetGroup,
      enableSizeFinder: true,
      reductionsState: useReductionsStateRequestParameter(),
      selectedCouponPromotionId: selectedCouponPromotionId ?? null,
      basketToken,
      preferredProductImageType: useProtoProductImageType(),
      enableFitmatch: false,
      fitmatchScanningCapableDevice: false,
    },
    {
      disabled: disabled || refetchesDisabled,
      keepPreviousData,
      section,
      refetchOnWindowFocus: true,
      staleTime: FIVE_MINUTES_IN_MS,
      // There is no need to refetch this after product was added to basket
      // modal is called on demand so it means it will always have latest token available and after product is added to basket we shouldn't call it again
      transformCacheKey: (config: CacheKeyConfig) => {
        return {
          ...config,
          params: {
            ...config.params,
            basketToken: undefined,
          },
        };
      },
    },
  );
};

export const useImagesSection = (productId: number, disabled?: boolean) => {
  return useGetMinimalProductBulkSection('imagesSection', productId, disabled);
};

export const usePriceAndBrandSection = (
  productId: number,
  disabled?: boolean,
) => {
  return useGetMinimalProductBulkSection(
    'priceAndBrandSection',
    productId,
    disabled,
  );
};

export const useMinimalGetProductBulkBadges = ({
  productId,
  disabled,
}: {
  productId: number;
  disabled?: boolean;
}) => {
  const imagesSection = useImagesSection(productId, disabled);
  const imagesSectionBadges = imagesSection.data?.badges;
  const priceAndBrand = usePriceAndBrandSection(productId, disabled);
  const combinedBadges = useGetBadgesFromBulkRequest({
    imagesSectionBadges,
    priceAndBrand: priceAndBrand.data,
  });

  return {
    badges: combinedBadges,
    isFetching: imagesSection.isFetching || priceAndBrand.isFetching,
  };
};

export const useProductSelectionSection = (productId: number) => {
  return useGetMinimalProductBulkSection('productSelectionSection', productId);
};

export const useSizesSection = (
  productId: number,
  currentSize?: {
    selectedSize: SelectedSize;
    setSelectedSize: SetSelectedSize;
  },
  disabled?: boolean,
) => {
  const request = useGetMinimalProductBulkSection(
    'sizesSection',
    productId,
    disabled,
  );

  useEffect(() => {
    if (!currentSize || !request?.data) {
      return;
    }

    const {selectedSize, setSelectedSize} = currentSize;
    const updatedSize = getCurrentSelectedSizeFromSizesSection(
      request.data,
      selectedSize,
    );

    if (updatedSize) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setSelectedSize(updatedSize);
    }
  }, [currentSize?.selectedSize?.sizeId, request?.data]);

  return request;
};

export const useLinksSection = (productId: number) => {
  return useGetMinimalProductBulkSection('linksSection', productId);
};

export const useProminentBannerSection = (productId: number) => {
  return useGetMinimalProductBulkSection('prominentBannerSection', productId);
};
